import { httpsCallable } from 'firebase/functions';
import { serverTimestamp } from 'firebase/firestore';

import { functions } from '../firebase/config';
import { isFestpreisVorab } from './preismodell';

/**
 * Festpreis vor der Fahrt aus dem Portal (Mietwagen, Disponent bucht).
 * nextcab_mobile/docs/general/mietwagen_festpreis_modus_konzept.md §14.5, E9
 *
 * Das Portal holt dasselbe verbindliche Angebot wie die Kunden-App
 * (Cloud Function `quoteRide`, gleiche Strecke inkl. Place-ID und
 * Wende-Prüfung, Verkehr zur Abholzeit, Festpreisliste, Mindestfahrpreis,
 * Zuschläge, Anfahrt). Der Disponent darf den Betrag ohne Grenze
 * überschreiben; der berechnete Preis wird mitgespeichert und vom Trigger
 * `festpreisVorabOnRideCreate` gegen das Angebot geprüft.
 *
 * Die Fahrer-App sperrt den Betrag im Abschluss-Sheet (`festpreisVorabCents`).
 * Spiegelt die Felder von FestpreisQuote.rideFields (Kunden-App,
 * lib/services/festpreis_quote_service.dart).
 */

/** Gilt der Festpreis für diese Fahrt? Ohne Ziel und bei Krankenfahrten/Daueraufträgen bleibt der Preis offen. */
export const festpreisVorabFuerFahrt = (general, { hasDestination, isKrankenfahrt, isDauerauftrag }) =>
  isFestpreisVorab(general) && !!hasDestination && !isKrankenfahrt && !isDauerauftrag;

const toPoint = (location, address) => ({
  lat: location.latitude,
  lng: location.longitude,
  address: address || '',
  ...(location.placeId ? { placeId: location.placeId } : {}),
});

/**
 * Eingaben, die den Preis bestimmen. Der Fahrzeugtyp gehört NICHT dazu: Das
 * Angebot enthält die Preise aller Fahrzeugtypen.
 */
export const angebotKey = ({ pickup, destination, stops = [], scheduledTime = null, specialCapabilities = [] }) => [
  pickup ? `${pickup.latitude},${pickup.longitude},${pickup.placeId || ''}` : '-',
  stops.map((s) => `${s.location?.latitude},${s.location?.longitude}`).join('>') || '-',
  destination ? `${destination.latitude},${destination.longitude},${destination.placeId || ''}` : '-',
  scheduledTime ? new Date(scheduledTime).toISOString() : 'now',
  [...specialCapabilities].sort().join(',') || '-',
].join('|');

/**
 * Verbindliches Angebot holen (speichert ein `rideQuotes`-Dokument, 10 min gültig).
 * `scheduledTime` nur bei Vorbestellungen.
 */
export const holeFestpreisAngebot = async ({
  pickup, pickupAddress, destination, destinationAddress, stops = [], scheduledTime = null, specialCapabilities = [],
}) => {
  const response = await httpsCallable(functions, 'quoteRide')({
    pickup: toPoint(pickup, pickupAddress),
    destination: toPoint(destination, destinationAddress),
    stops: stops.map((s) => ({
      lat: s.location.latitude,
      lng: s.location.longitude,
      address: s.address || '',
    })),
    scheduledTimeMs: scheduledTime ? new Date(scheduledTime).getTime() : null,
    specialCapabilities,
  });
  return {
    ...response.data,
    key: angebotKey({ pickup, destination, stops, scheduledTime, specialCapabilities }),
  };
};

/** Noch mindestens 30 Sekunden gültig (Puffer fürs Speichern, wie die App). */
export const angebotGueltig = (angebot) =>
  !!angebot?.quoteId && typeof angebot.expiresAtMs === 'number' && Date.now() < angebot.expiresAtMs - 30_000;

export const angebotPreis = (angebot, vehicleType) =>
  angebot?.preise?.[String(parseInt(vehicleType, 10) || 1)] || null;

export const formatEuroCents = (cents) => (cents / 100).toFixed(2).replace('.', ',');

/** „12,50" / „12.5" / „12" → Cent; leer oder ungültig → null. */
export const parseEuroCents = (value) => {
  const text = String(value ?? '').trim().replace(/\s|€/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const cents = Math.round(parseFloat(text) * 100);
  return cents > 0 ? cents : null;
};

/** Zusammensetzung für die Anzeige („Tarif 9,40 € + Zuschlag 5,00 €"). */
export const angebotAufteilung = (preis) => {
  if (!preis) return '';
  const teile = [
    preis.quelle === 'festpreisliste'
      ? `Festpreisliste${preis.festpreisLabel ? ` (${preis.festpreisLabel})` : ''} ${formatEuroCents(preis.basisCents)} €`
      : `Tarif ${formatEuroCents(preis.basisCents)} €`,
  ];
  if (preis.zuschlagCents > 0) teile.push(`Zuschlag ${formatEuroCents(preis.zuschlagCents)} €`);
  if (preis.anfahrtCents > 0) teile.push(`Anfahrt ${formatEuroCents(preis.anfahrtCents)} €`);
  const summe = (preis.basisCents || 0) + (preis.zuschlagCents || 0) + (preis.anfahrtCents || 0);
  const hinweis = preis.untergrenze === 'mindestpreis'
    ? ' · Mindestfahrpreis'
    : summe !== preis.cents ? ' · auf 10 Cent gerundet' : '';
  return teile.join(' + ') + hinweis;
};

/**
 * Felder für das Fahrtdokument. Koordinaten, Place-IDs und Ausstattung kommen
 * aus dem Angebot, damit der Trigger sie wiederfindet.
 * `overrideCents` = vom Disponenten überschriebener Betrag (oder null).
 */
export const festpreisRideFields = (angebot, vehicleType, { overrideCents = null, disponentId = null } = {}) => {
  const preis = angebotPreis(angebot, vehicleType);
  if (!preis) throw new Error('Kein Festpreis für diesen Fahrzeugtyp.');
  const cents = overrideCents ?? preis.cents;
  const e = angebot.eingaben || {};
  return {
    priceType: 'festpreis_vorab',
    quoteId: angebot.quoteId,
    festpreisVorabCents: cents,
    festpreisBerechnetCents: preis.cents,
    festpreisVorabQuelle: 'disponent',
    festpreisVorabDisponentId: disponentId,
    festpreisVereinbartAt: serverTimestamp(),
    estimatedEarningEur: cents / 100,
    distanceKm: angebot.distanceKm,
    ...(angebot.durationMin > 0 ? { estimatedTimeMinutes: angebot.durationMin } : {}),
    startLatitude: e.pickup.lat,
    startLongitude: e.pickup.lng,
    destinationLatitude: e.destination.lat,
    destinationLongitude: e.destination.lng,
    // Nur vom Server geprüfte Place-IDs (Phase 2d/2e), sonst null.
    startPlaceId: e.pickup.placeId || null,
    destinationPlaceId: e.destination.placeId || null,
    specialCapabilities: Array.isArray(e.specialCapabilities) ? e.specialCapabilities : [],
    // Alter Weg „Festpreis" (freier Betrag) geht im Überschreiben auf.
    isFestpreis: false,
  };
};

/**
 * Vereinbarter Preis ohne Angebot (Berechnung fehlgeschlagen). Der Trigger
 * markiert die Fahrt mit `festpreisPruefung: 'ohne_angebot'`; Zielwechsel
 * und früherer Ausstieg rechnen dann mit der geraden Linie über die Halte
 * statt mit der gespeicherten Route (requoteRide).
 */
export const festpreisOhneAngebotFields = (cents, disponentId = null) => ({
  priceType: 'festpreis_vorab',
  festpreisVorabCents: cents,
  festpreisBerechnetCents: null,
  festpreisVorabQuelle: 'disponent',
  festpreisVorabDisponentId: disponentId,
  festpreisVereinbartAt: serverTimestamp(),
  estimatedEarningEur: cents / 100,
  isFestpreis: false,
});
